import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, CheckCircle2, CreditCard, XCircle, Wrench } from "lucide-react";
import { toast } from "sonner";
import RobotsTag from "../seo/RobotsTag";
import { fmtEUR } from "../../lib/invoiceConstants";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/payments`;
const STATUS_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/payments/status`;

const PublicPaymentPage = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [pay, setPay] = useState(null); // null=loading, false=error
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/${token}`);
      if (!res.ok) { setPay(false); return; }
      setPay(await res.json());
    } catch { setPay(false); }
  }, [token]);

  const pollStatus = useCallback(async (attempt = 0) => {
    if (attempt >= 6) { setChecking(false); return; }
    setChecking(true);
    try {
      const res = await fetch(`${STATUS_BASE}/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === "paid") {
        toast.success("Paiement réussi. Merci !");
        setChecking(false);
        load();
        return;
      }
      setTimeout(() => pollStatus(attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  }, [sessionId, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (sessionId) pollStatus(0); }, [sessionId, pollStatus]);

  const payNow = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${BASE}/${token}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) { toast.error(data.detail || "Impossible de démarrer le paiement."); setPaying(false); return; }
      window.location.href = data.url;
    } catch {
      toast.error("Erreur de paiement.");
      setPaying(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-100 py-10 px-4">
      <RobotsTag content="noindex, nofollow" />
      <Helmet><title>Paiement – NGL SERVICES</title></Helmet>
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="bg-[hsl(207,78%,42%)] rounded-lg p-2"><Wrench className="w-5 h-5 text-white" /></div>
          <span className="font-bold text-slate-800">NGL SERVICES</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (pay === null) return <Shell><div className="flex justify-center p-10"><Loader2 className="w-8 h-8 animate-spin text-[hsl(207,78%,42%)]" /></div></Shell>;
  if (pay === false) return <Shell><Card className="p-8 text-center" data-testid="payment-not-found"><XCircle className="w-10 h-10 mx-auto text-red-400 mb-3" /><p>Lien de paiement invalide.</p></Card></Shell>;

  const paid = pay.status === "paye";

  return (
    <Shell>
      <Card className="overflow-hidden" data-testid="public-payment-card">
        <div className="bg-[hsl(215,25%,15%)] text-white p-6">
          <p className="text-sm text-slate-300">Paiement groupé</p>
          <p className="text-2xl font-bold font-mono">{pay.number}</p>
          {pay.buyerName && <p className="text-slate-200 mt-1">{pay.buyerName}</p>}
        </div>
        <div className="p-6 space-y-4">
          {checking && (
            <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 rounded p-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Vérification du paiement...
            </div>
          )}
          <div className="space-y-1">
            {pay.invoices.map((inv) => (
              <div key={inv.id} className="flex justify-between text-sm">
                <span className="font-mono">{inv.number || "Facture"}</span>
                <span className="font-medium">{fmtEUR(inv.amount)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between border-t pt-2 font-bold text-lg">
            <span>Total à payer</span>
            <span className="text-[hsl(207,78%,42%)]" data-testid="payment-total">{fmtEUR(pay.totalAmount)}</span>
          </div>

          {paid ? (
            <div className="bg-emerald-50 rounded-lg p-4 text-center" data-testid="payment-paid">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
              <p className="font-semibold text-emerald-700">Paiement effectué</p>
              {pay.paidAt && <p className="text-xs text-muted-foreground">Le {(pay.paidAt || "").slice(0, 16).replace("T", " ")}</p>}
            </div>
          ) : (
            <Button className="w-full h-12 btn-primary text-base" onClick={payNow} disabled={paying} data-testid="pay-now-button">
              {paying ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
              Payer {fmtEUR(pay.totalAmount)}
            </Button>
          )}
          <p className="text-[11px] text-center text-muted-foreground">Paiement sécurisé par Stripe · NGL SERVICES</p>
        </div>
      </Card>
    </Shell>
  );
};

export default PublicPaymentPage;
